import type { ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";

// "unknown" is distinct from "normal" — see computeStatus below. Every
// consumer filtering/counting "abnormal" must exclude it explicitly rather
// than relying on `!== "normal"`, the same fix applied to
// diagnostics/utils/adaptDiagnostics.ts's identical status computation.
export type LabValueStatus = "normal" | "high" | "low" | "abnormal" | "unknown";

export interface LabValue {
  id: string;
  label: string;
  value: string;
  unit: string;
  status: LabValueStatus;
  rangeLabel: string; // "70-100 mg/dL", "" if no range on record
  date: string; // ISO yyyy-mm-dd
}

function toDateOnly(value: unknown): string {
  return typeof value === "string" ? value.slice(0, 10) : "";
}

/**
 * Observation.interpretation carries whatever the vendor sent — sometimes a
 * standard HL7 code (H/L/HH/LL/A/N), sometimes free text ("High"/"Critical
 * Low"). When it's blank, the only honest fallback is comparing the numeric
 * value against the record's own reference range; if there's no range
 * either (or no numeric value at all), this is genuinely unassessed — "we
 * don't know", not "confirmed normal" — so it returns "unknown" rather than
 * inventing either flag. Collapsing that into "normal" used to make an
 * entirely unassessed value look confirmed-normal in every downstream
 * abnormal-count/filter (see isAbnormalLabValueStatus below).
 */
export function isAbnormalLabValueStatus(status: LabValueStatus): boolean {
  return status === "high" || status === "low" || status === "abnormal";
}

function computeStatus(f: Record<string, unknown>): LabValueStatus {
  const raw = String(f.interpretation ?? "").trim().toLowerCase();
  if (raw) {
    if (raw === "h" || raw === "hh" || raw.includes("high")) return "high";
    if (raw === "l" || raw === "ll" || raw.includes("low")) return "low";
    if (raw === "a" || raw === "aa" || raw.includes("abnormal") || raw.includes("critical")) return "abnormal";
    if (raw === "n" || raw.includes("normal")) return "normal";
  }
  const value = parseFloat(String(f.value_quantity ?? ""));
  const low = parseFloat(String(f.reference_range_low ?? ""));
  const high = parseFloat(String(f.reference_range_high ?? ""));
  const hasRange = !Number.isNaN(low) || !Number.isNaN(high);
  if (!Number.isNaN(value) && hasRange) {
    if (!Number.isNaN(low) && value < low) return "low";
    if (!Number.isNaN(high) && value > high) return "high";
    return "normal";
  }
  return "unknown";
}

function buildRangeLabel(f: Record<string, unknown>, unit: string): string {
  const low = f.reference_range_low as string | undefined;
  const high = f.reference_range_high as string | undefined;
  if (!low && !high) return "";
  return `${low ?? "?"}-${high ?? "?"}${unit ? ` ${unit}` : ""}`;
}

/**
 * There's no reliable Observation -> DiagnosticReport link in the current
 * pipeline (Epic's mapper only stores a result count, not real references),
 * so this deliberately does NOT try to attach a specific panel/report name.
 * Grouping "which values belong together" is done by the caller matching on
 * `date` instead (same-day observations = same lab draw).
 */
export function adaptObservationValue(record: ParsedRecord): LabValue | null {
  const f = record.fields;
  const date = toDateOnly(f.effective_date);
  const rawValue = (f.value_quantity as string) ?? (f.value_string as string) ?? (f.value_codeable_text as string);
  if (!date || rawValue === undefined || rawValue === null || rawValue === "") return null;
  const unit = (f.value_unit as string) || "";
  return {
    id: record.id,
    label: (f.display_name as string) || "Lab Value",
    value: String(rawValue),
    unit,
    status: computeStatus(f),
    rangeLabel: buildRangeLabel(f, unit),
    date,
  };
}
